// ./src/hooks/useUsers.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  User,
  UserInput,
  UserFilter,
  UserUpdateManyInput,
  UserService,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseUsersOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<UserFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;
}

export interface UseUsersReturn {
  // Data state
  user: User | null;
  users: User[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<UserFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingUserId: string | null;
  selectedUser: User | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<UserFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<User[]>;
  create: (params: UserInput) => Promise<User | null>;
  createMany: (params: UserInput[]) => Promise<User[] | null>;
  update: (id: string, params: UserInput) => Promise<User | null>;
  updateMany: (params: UserUpdateManyInput[]) => Promise<User[] | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<UserFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedUser: (user: User | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values (for selected/current user)
  fullName: string | null;
  displayName: string | null;
  isEmailConfirmed: boolean;
  hasToken: boolean;
  usersByStatus: Record<string, User[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "username", direction: "asc" }];

export const useUsers = (options: UseUsersOptions = {}): UseUsersReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<UserFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => users.length === 0 && !loading,
    [users.length, loading]
  );

  const fullName = useMemo(() => {
    const currentUser = selectedUser || user;
    if (!currentUser) return null;
    const parts = [
      currentUser.firstName,
      currentUser.middleName,
      currentUser.lastName,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : null;
  }, [
    selectedUser?.firstName,
    selectedUser?.middleName,
    selectedUser?.lastName,
    user?.firstName,
    user?.middleName,
    user?.lastName,
  ]);

  const displayName = useMemo(() => {
    const currentUser = selectedUser || user;
    if (!currentUser) return null;
    return (
      fullName || currentUser.username || currentUser.email || "Unknown User"
    );
  }, [
    fullName,
    selectedUser?.username,
    selectedUser?.email,
    user?.username,
    user?.email,
  ]);

  const isEmailConfirmed = useMemo(() => {
    const currentUser = selectedUser || user;
    return currentUser?.emailConfirmation ?? false;
  }, [selectedUser?.emailConfirmation, user?.emailConfirmation]);

  const hasToken = useMemo(() => {
    const currentUser = selectedUser || user;
    return Boolean(currentUser?.token);
  }, [selectedUser?.token, user?.token]);

  const usersByStatus = useMemo(() => {
    return users.reduce((acc, user) => {
      const status = String(user.status || "unknown");
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(user);
      return acc;
    }, {} as Record<string, User[]>);
  }, [users]);

  // Helper function to update user in list if it exists, or add it if not
  const updateOrAddUserInList = useCallback((updatedUser: User) => {
    setUsers((prev) => {
      const existingIndex = prev.findIndex((u) => u.id === updatedUser.id);
      if (existingIndex >= 0) {
        const newUsers = [...prev];
        newUsers[existingIndex] = updatedUser;
        return newUsers;
      } else {
        return [updatedUser, ...prev];
      }
    });
  }, []);

  // Helper function to remove user from list if it exists
  const removeUserFromList = useCallback((userId: string) => {
    setUsers((prev) => prev.filter((u) => u.id !== userId));
  }, []);

  // Helper function to remove multiple users from list
  const removeUsersFromList = useCallback((userIds: string[]) => {
    const idsSet = new Set(userIds);
    setUsers((prev) => prev.filter((u) => !idsSet.has(u.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setUser(null);
    setUsers([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingUserId(null);
    setSelectedUser(null);
  }, []);

  // Get single user (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await UserService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const userData = result.data[0] as User;
          setUser(userData);
          updateOrAddUserInList(userData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load user";
          setLastError(errorMessage);
          setUser(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load user";
        setLastError(errorMessage);
        setUser(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddUserInList]
  );

  // List users (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<UserFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setUsers([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await UserService.list(
          filter as UserFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newUsers = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setUsers((prev) => {
              const existingIds = new Set(prev.map((u) => u.id));
              const uniqueNewUsers = newUsers.filter(
                (u) => !existingIds.has(u.id)
              );
              return [...prev, ...uniqueNewUsers];
            });
          } else {
            setUsers(newUsers);
          }

          setHasMore(newUsers.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load users";
          setLastError(errorMessage);
          if (!isAppending) setUsers([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load users";
        setLastError(errorMessage);
        if (!isAppending) setUsers([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many users by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<User[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await UserService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedUsers = Array.isArray(result.data) ? result.data : [];
          loadedUsers.forEach((user) => {
            updateOrAddUserInList(user);
          });
          setLastError(null);
          return loadedUsers;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load users";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load users";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddUserInList]
  );

  // Create user (matching API method name)
  const create = useCallback(
    async (params: UserInput): Promise<User | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await UserService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newUser = result.data[0] as User;
          setUser(newUser);
          setUsers((prev) => [newUser, ...prev]);
          setLastError(null);
          return newUser;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create user";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create user";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Create many users (matching API method name)
  const createMany = useCallback(
    async (params: UserInput[]): Promise<User[] | null> => {
      if (params.length === 0) {
        return [];
      }

      setIsCreating(true);
      setLastError(null);

      try {
        const result = await UserService.createMany(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newUsers = Array.isArray(result.data) ? result.data : [];
          setUsers((prev) => [...newUsers, ...prev]);
          setLastError(null);
          return newUsers;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create users";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create users";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update user (matching API method name)
  const update = useCallback(
    async (id: string, params: UserInput): Promise<User | null> => {
      if (!id) {
        setLastError("User ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await UserService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedUser = result.data[0] as User;
          setUser(updatedUser);
          updateOrAddUserInList(updatedUser);
          setLastError(null);
          return updatedUser;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update user";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update user";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddUserInList]
  );

  // Update many users (matching API method name)
  const updateMany = useCallback(
    async (params: UserUpdateManyInput[]): Promise<User[] | null> => {
      if (params.length === 0) {
        return [];
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await UserService.updateMany(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedUsers = Array.isArray(result.data) ? result.data : [];

          // Update users in the list
          setUsers((prev) =>
            prev.map((existingUser) => {
              const updated = updatedUsers.find(
                (u) => u.id === existingUser.id
              );
              return updated || existingUser;
            })
          );

          // Update single user if it matches any of the updated users
          setUser((prev) => {
            if (!prev) return prev;
            const updatedSingleUser = updatedUsers.find(
              (u) => u.id === prev.id
            );
            return updatedSingleUser || prev;
          });

          setLastError(null);
          return updatedUsers;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update users";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update users";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  // Remove user (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("User ID is required for removal");
        return false;
      }

      setRemovingUserId(id);
      setLastError(null);

      try {
        const result = await UserService.remove({ id });

        if (result.code === OP_RESULT_CODES.OK) {
          setUser(null);
          removeUserFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove user";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove user";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingUserId(null);
      }
    },
    [removeUserFromList]
  );

  // Remove many users (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one User ID is required for removal");
        return false;
      }

      setLastError(null);

      const userWhereInputs = ids.map((id) => ({ id }));

      try {
        const result = await UserService.removeMany(userWhereInputs);

        if (result.code === OP_RESULT_CODES.OK) {
          setUser(null);
          removeUsersFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove users";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove users";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeUsersFromList]
  );

  // Load more users
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh users
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    user,
    users,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingUserId,
    selectedUser,
    get,
    list,
    getMany,
    create,
    createMany,
    update,
    updateMany,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedUser,
    reset,
    fullName,
    displayName,
    isEmailConfirmed,
    hasToken,
    usersByStatus,
    lastError,
    clearError,
  };
};
